import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import useSencoPupils from '../../hooks/useSencoPupils';
import useMyReferralDocuments, { useDocumentRemoval } from '../../hooks/useMyReferralDocuments';
import { getOrgConfig } from '../../utils/orgResolution';
import { buildFamilyChild, familyUserName } from '../../utils/familyPortal';
import {
  ADD_LOCKED_UNTIL_CONSENT,
  CAP_REACHED,
  DOCUMENTS_LOAD_FAILED,
  DOCUMENTS_NONE_SENT,
  DOCUMENTS_NONE_YET,
  REMOVE_CONFIRM,
  REMOVE_TITLE,
  removeBody,
} from '../../utils/referralDocumentPortal';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourDocumentRow from '../../components/harbour/HarbourDocumentRow';
import { addLockedForConsent, atCap, canAddDocument, documentsHelperLines } from '../../components/harbour/HarbourDocumentsBlock';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import FamilyConfirmSheet from './FamilyConfirmSheet';
import FamilyLoadError from './FamilyLoadError';
import FamilyLoadingState from './FamilyLoadingState';
import FamilyPortalShell from './FamilyPortalShell';
import useIsDesktop from './useIsDesktop';
import '../../components/harbour/harbourDocuments.css';
import './familyDocuments.css';

/*
 * Family portal · Documents, phone (TDI-579, Figma D-06 Q2, Q4, Q9, Q10):
 * the parent or carer's own documents on the referral, opened from the
 * Documents row on their child's record. The sticky bar holds the one gold
 * action, "Add a document"; at the cap it is replaced by a plain message,
 * and once the referral is sent the screen is read-only (TDI-580).
 *
 * On desktop the documents live in the record flyout's Documents block
 * (D-05), so this address goes back to the record.
 */
export default function FamilyDocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientRecordId } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();
  const base = `/rms/family/child/${patientRecordId}`;

  useEffect(() => {
    if (claims && claims.userType && claims.userType !== 'RMS Legal Guardian') {
      navigate('/rms/dashboard', { replace: true });
    }
  }, [claims, navigate]);
  useEffect(() => {
    if (isDesktop) navigate(base, { replace: true, state: { stay: true } });
  }, [isDesktop, navigate, base]);

  const pupil = useMemo(() => pupils.find((p) => p.id === patientRecordId) || null, [pupils, patientRecordId]);
  const child = useMemo(() => buildFamilyChild(pupil), [pupil]);
  const documentsOn = Boolean(child && child.referral && !child.referral.stopped);
  const docs = useMyReferralDocuments({ orgId, patientRecordId, enabled: documentsOn });

  const [notice, setNotice] = useState(() => (
    location.state && location.state.documentNotice
      ? { kind: 'info', title: location.state.documentNotice }
      : null
  ));
  const noticeInHistory = Boolean(location.state && location.state.documentNotice);
  useEffect(() => {
    if (noticeInHistory) navigate(location.pathname, { replace: true, state: null });
  }, [noticeInHistory, navigate, location.pathname]);
  const removal = useDocumentRemoval({ remove: docs.remove, reload: docs.reload, onNotice: setNotice });

  const userName = familyUserName(user, pupils);
  const back = { to: base, label: 'Back' };

  if (loading) {
    return <FamilyPortalShell userName={userName} back={back}><FamilyLoadingState /></FamilyPortalShell>;
  }
  if (error || !pupil) {
    return <FamilyPortalShell userName={userName} back={back}><FamilyLoadError onRetry={() => reload()} /></FamilyPortalShell>;
  }

  const summary = docs.summary;
  const documents = (summary && summary.documents) || [];
  const notOnReferral = !documentsOn || (summary && summary.onReferralPathway === false);
  const lockedAdd = addLockedForConsent(summary);
  // While adding is locked, the cap and until-sent lines would contradict
  // the lock, so the locked bar's line stands alone.
  const helper = lockedAdd ? [] : documentsHelperLines(summary).filter((line) => line !== CAP_REACHED);

  return (
    <FamilyPortalShell userName={userName} back={back}>
      <div className="family-docs">
        <header className="flex flex-col gap-2">
          <p className="family-docs__eyebrow">
            <HarbourPathwayMark pathway="referral" />
            <span>{`Referral pathway · ${pupil.name}`}</span>
          </p>
          <h1 className="family-docs__title">Documents</h1>
        </header>

        {notice && (
          <HarbourNotice kind={notice.kind} title={notice.title} onClose={() => setNotice(null)}>
            {notice.body || null}
          </HarbourNotice>
        )}

        {notOnReferral ? (
          <p className="family-docs__line family-docs__line--ink">Documents can only be added to a referral.</p>
        ) : !summary && !docs.error ? (
          <>
            <div className="harbour-skeleton h-[96px] animate-pulse" aria-hidden="true" />
            <p className="sr-only" role="status">Loading documents</p>
          </>
        ) : docs.error && !summary ? (
          <div className="flex flex-col items-start gap-2">
            <p className="family-docs__line family-docs__line--ink">{DOCUMENTS_LOAD_FAILED}</p>
            <HarbourTextLink size="standard" onClick={() => docs.reload()}>Try again</HarbourTextLink>
          </div>
        ) : (
          <>
            {documents.length > 0 ? (
              <ul className="family-docs__rows">
                {documents.map((d) => (
                  <HarbourDocumentRow
                    key={d.id}
                    document={d}
                    layout="phone"
                    onRemove={summary.sent ? null : removal.setRemoving}
                  />
                ))}
              </ul>
            ) : (
              <p className="family-docs__line">{summary.sent ? DOCUMENTS_NONE_SENT : DOCUMENTS_NONE_YET}</p>
            )}
            {helper.map((line) => <p key={line} className="family-docs__line">{line}</p>)}
          </>
        )}

        {!notOnReferral && canAddDocument(summary) && (
          <div className="harbour-doc-bar">
            <HarbourButton kind="family" className="harbour-doc-bar__cta" onClick={() => navigate(`${base}/documents/add`)}>
              Add a document
            </HarbourButton>
          </div>
        )}
        {!notOnReferral && atCap(summary) && (
          <div className="harbour-doc-bar">
            <p className="harbour-doc-bar__message">{CAP_REACHED}</p>
          </div>
        )}
        {!notOnReferral && lockedAdd && (
          <div className="harbour-doc-bar harbour-doc-bar--locked">
            <HarbourButton kind="family" className="harbour-doc-bar__cta" leadingIcon="lock" disabled aria-disabled="true">
              Add a document
            </HarbourButton>
            <p className="harbour-doc-bar__message">{ADD_LOCKED_UNTIL_CONSENT}</p>
          </div>
        )}
      </div>

      {removal.removing && (
        <FamilyConfirmSheet
          title={REMOVE_TITLE}
          confirmLabel={REMOVE_CONFIRM}
          confirmKind="warning"
          busy={removal.busy}
          onConfirm={removal.confirm}
          onClose={removal.cancel}
        >
          <p className="harbour-body-16">{removeBody(removal.removing.description)}</p>
        </FamilyConfirmSheet>
      )}
    </FamilyPortalShell>
  );
}
